import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Divider,
  Tooltip,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useCoins } from '../contexts/CoinContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { getTransactions } from '../services/api';
import { format } from 'date-fns';
import CountUp from 'react-countup';
import StarIcon from '@mui/icons-material/Star';

const Usage = () => {
  const { user } = useAuth();
  const { coins } = useCoins();
  const { isActive } = useSubscription();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsageData();
  }, []);

  const loadUsageData = async () => {
    try {
      setLoading(true);
      const transactionsData = await getTransactions();
      setTransactions(transactionsData);
    } catch (error) {
      console.error('Error loading usage data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTransactionColor = (amount, type) => {
    if (amount === 0) return 'text.secondary';
    if (type === 'credit_usage') return 'error.main';
    if (amount > 0) return 'success.main';
    return 'error.main';
  };

  const formatTransactionAmount = (amount, type) => {
    if (amount === 0) return 'Free';
    if (type === 'credit_usage') return `-${Math.abs(amount)}`;
    if (amount > 0) return `+${amount}`;
    return amount.toString();
  };

  const getChipColor = (amount, type) => {
    if (amount === 0) return 'default';
    if (type === 'credit_usage') return 'error';
    if (amount > 0) return 'success';
    return 'error';
  };

  return (
    <Box>
      {loading ? (
        <LinearProgress />
      ) : (
        <Grid container spacing={3}>
          {/* Credit Balance Card */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Current Balance
                </Typography>
                <Typography variant="h3" color="primary">
                  {coins}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Available credits
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Usage Estimates Card */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Estimated Usage with Current Balance
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {[
                  {
                    model: 'GPT-4o',
                    tokensPerCredit: 4000, // 0.25 per 1k tokens
                    estimatedTokens: coins * 4000,
                    estimatedWords: Math.floor((coins * 4000) * 0.75), // Approx. 0.75 words per token
                    icon: '🤖',
                    isPremium: true
                  },
                  {
                    model: 'GPT-4o-mini',
                    tokensPerCredit: 6667, // 0.15 per 1k tokens
                    estimatedTokens: coins * 6667,
                    estimatedWords: Math.floor((coins * 6667) * 0.75),
                    icon: '🚀',
                    isPremium: false
                  },
                  {
                    model: 'O3-mini',
                    tokensPerCredit: 2000, // 0.50 per 1k tokens
                    estimatedTokens: coins * 2000,
                    estimatedWords: Math.floor((coins * 2000) * 0.75),
                    icon: '⚡',
                    isPremium: true
                  },
                  {
                    model: 'Claude 3.5 Sonnet',
                    tokensPerCredit: 1000, // 1.0 per 1k tokens
                    estimatedTokens: coins * 1000,
                    estimatedWords: Math.floor((coins * 1000) * 0.75),
                    icon: '🧠',
                    isPremium: true
                  },
                  {
                    model: 'Flux Pro 1.1',
                    imagesPerCredit: 1,
                    estimatedMessages: coins,
                    icon: '🎨',
                    isPremium: false
                  }
                ].map((model) => (
                  <Grid item xs={12} sm={6} md={3} key={model.model}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Stack spacing={1}>
                          <Typography variant="h3" sx={{ mb: 1 }}>
                            {model.icon}
                          </Typography>
                          <Typography variant="h6" noWrap sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {model.model}
                            {model.isPremium && (
                              <Tooltip title="Premium model" arrow>
                                <StarIcon
                                  sx={{ 
                                    fontSize: '1.2rem',
                                    color: 'primary.main'
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Typography>
                          {model.tokensPerCredit ? (
                            <>
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  Estimated capacity:
                                </Typography>
                                <Typography variant="h5" color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                                  {model.model === 'GPT-4o-mini' && isActive ? (
                                    '∞ unlimited'
                                  ) : (
                                    <>
                                      ~<CountUp
                                        end={Math.round(model.estimatedTokens / 1000)}
                                        duration={2}
                                        separator=","
                                      />K
                                    </>
                                  )}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  tokens
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  or approximately:
                                </Typography>
                                <Typography variant="h5" color="primary.dark" sx={{ display: 'flex', alignItems: 'center' }}>
                                  {model.model === 'GPT-4o-mini' && isActive ? (
                                    '∞ unlimited'
                                  ) : (
                                    <>
                                      ~<CountUp
                                        end={Math.round(model.estimatedWords / 1000)}
                                        duration={2}
                                        separator=","
                                      />K
                                    </>
                                  )}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  words
                                </Typography>
                              </Box>
                            </>
                          ) : (
                            <>
                              <Typography variant="body2" color="text.secondary">
                                Estimated usage:
                              </Typography>
                              <Typography variant="h4" color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                                ~<CountUp
                                  end={model.estimatedMessages}
                                  duration={2}
                                  separator=","
                                />
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                images
                              </Typography>
                            </>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Recent Transactions */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Recent Transactions
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((transaction) => {
                      const type = transaction.type.toLowerCase();
                      return (
                        <TableRow key={transaction.id}>
                          <TableCell>
                            {format(new Date(transaction.date), 'MMM d, yyyy HH:mm')}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={transaction.type}
                              color={getChipColor(transaction.amount, type)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{transaction.description}</TableCell>
                          <TableCell align="right">
                            <Typography
                              color={getTransactionColor(transaction.amount, type)}
                            >
                              {formatTransactionAmount(transaction.amount, type)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Usage; 